/**
 * The thrown rock, one truth for both sides (2026-10-27 feel pass). Holding
 * Force grows the rock in front of the caster; how long it was held sets the
 * charge, and the charge sets the throw: a tap lobs a pebble a few units, a
 * full charge hurls a boulder across the room. It flies along the aim to
 * exactly `rockLanding` (unless a body or a wall stops it first) on the arc
 * `rockHeight`. The server launches it with these numbers, the client
 * predicts its own rock and draws the aim preview with them.
 */

import type { Arena } from './arena.js';
import { BOULDER, CHARGE_TIME } from './constants.js';
import type { Vec2 } from './math.js';
import { clamp, fromAngle, lerp, segCircle, segRect } from './math.js';

/** Charge 0..1 for a hold of `seconds`. */
export function chargeFromHold(seconds: number): number {
  return clamp(seconds / CHARGE_TIME, 0, 1);
}

/** Radius of the rock at a charge (it grows in the hand; the thrown rock keeps it, for drawing and hits). */
export function rockRadius(charge: number): number {
  return lerp(BOULDER.minRadius, BOULDER.maxRadius, clamp(charge, 0, 1));
}

export interface RockFlight { speed: number; range: number; radius: number }

/** Speed, distance and size of a throw at `charge`; `fullRange` is the spell's full-charge distance. */
export function rockFlight(fullRange: number, charge: number): RockFlight {
  const c = clamp(charge, 0, 1);
  return { speed: lerp(BOULDER.minSpeed, BOULDER.speed, c), range: lerp(BOULDER.minRange, fullRange, c), radius: rockRadius(c) };
}

/** Direct hit damage at a charge (DESIGN 2.5: direct x (1 + 3.4 charge)). */
export function rockDirect(direct: number, charge: number): number {
  return direct * (1 + BOULDER.directChargeBonus * clamp(charge, 0, 1));
}

/** Where the rock is held and leaves from: just outside the caster's body along the aim. */
export function rockMuzzle(pos: Vec2, aim: number, casterRadius: number, radius: number): Vec2 {
  const d = fromAngle(aim, casterRadius + radius);
  return { x: pos.x + d.x, y: pos.y + d.y };
}

/** Where a throw at `charge` comes down when nothing is in the way. */
export function rockLanding(pos: Vec2, aim: number, casterRadius: number, fullRange: number, charge: number): Vec2 {
  const f = rockFlight(fullRange, charge);
  const m = rockMuzzle(pos, aim, casterRadius, f.radius);
  const d = fromAngle(aim, f.range);
  return { x: m.x + d.x, y: m.y + d.y };
}

/**
 * How far a rock of `radius` thrown from `start` along `aim` gets before the map stops it: a pillar, a wall block or
 * the map's edge, the same swept disc the server flies (sweep.ts; spell walls and barriers come and go, so the
 * preview leaves them out). `range` when nothing is in the way.
 */
export function rockReach(arena: Arena, start: Vec2, aim: number, range: number, radius: number): number {
  const end = { x: start.x + Math.cos(aim) * range, y: start.y + Math.sin(aim) * range };
  let t = 1;
  for (const pl of arena.pillars) {
    const h = segCircle(start, end, { x: pl.x, y: pl.y }, pl.r + radius);
    if (h.hit) t = Math.min(t, h.t);
  }
  for (const b of arena.blocks) {
    const h = segRect(start, end, b, radius);
    if (h) t = Math.min(t, h.t);
  }
  const minX = radius, maxX = arena.w - radius, minY = radius, maxY = arena.h - radius;
  if (end.x < minX && start.x > end.x) t = Math.min(t, (start.x - minX) / (start.x - end.x));
  if (end.x > maxX && end.x > start.x) t = Math.min(t, (maxX - start.x) / (end.x - start.x));
  if (end.y < minY && start.y > end.y) t = Math.min(t, (start.y - minY) / (start.y - end.y));
  if (end.y > maxY && end.y > start.y) t = Math.min(t, (maxY - start.y) / (end.y - start.y));
  return clamp(t, 0, 1) * range;
}

/** Height above the ground after `traveled` of a `range` throw: from hand height down to the ground, over an arc. */
export function rockHeight(traveled: number, range: number): number {
  const f = range > 0 ? clamp(traveled / range, 0, 1) : 1;
  const lift = BOULDER.arcBase + BOULDER.arcPerUnit * range;
  return BOULDER.handHeight * (1 - f) + lift * 4 * f * (1 - f);
}

/**
 * The hold the server throws with: the client's own measure when it is
 * within BOULDER.holdSlack of the server's (network jitter), clamped to that
 * window otherwise, the server's when the client sent none.
 */
export function settleHold(claimed: number | undefined, measured: number): number {
  if (claimed === undefined || !Number.isFinite(claimed)) return measured;
  return clamp(claimed, Math.max(0, measured - BOULDER.holdSlack), measured + BOULDER.holdSlack);
}
